import Alert from "@/components/alerts/Alert";
import useIsFlagEnabled from "@/hooks/useIsFlagEnabled";
import { useSession, useSessionMutate } from "@/security/RouteGuards";
import {
  PendingPaymentsList,
  PendingPaymentStatus,
  PendingPaymentType,
} from "@/types/PaymentMethodTypes";
import { PlanType } from "@/types/PlanTypes";
import { TransactionType } from "@/types/TransactionTypes";
import { CircularProgress, Snackbar, Typography } from "@mui/material";
import { captureException } from "@/observability/sentry";
import { useMatchRoute, useRouterState } from "@tanstack/react-router";
import { useParams } from "@tanstack/react-router";
import React, { useEffect } from "react";
import useSWR from "@/hooks/useSWR";
import { KeyedMutator } from "swr";

type PaymentPollingContextType = {
  startPolling: (paymentId: number) => void;
  transactionData: TransactionType | undefined;
  planResponse: PlanType | undefined;
  paymentData: PendingPaymentType | undefined;
  mutatePayment: KeyedMutator<PendingPaymentType>;
  mutateTransaction: KeyedMutator<TransactionType>;
  paymentId: number | null;
  status: PendingPaymentStatus | undefined;
  /**
   * The payment polling gave up on while it was still processing, so a
   * checkout can tell "still confirming" from a finished poll (R-2293).
   */
  timedOutPaymentId: number | null;
};

const PaymentPollingContext = React.createContext<
  PaymentPollingContextType | undefined
>(undefined);

/**
 * This hooks starts polling for payment status when mounted.
 *
 * The polling itself happens in a provider, so that even if user navigates away from the page,
 * the polling continues.
 */
export const usePaymentPollingContext = () => {
  const context = React.useContext(PaymentPollingContext);

  // `paymentid` is a path segment on /subscription/pending/$paymentid and a
  // query parameter elsewhere; Next merged both, path winning (ANO-112).
  const params = useParams({ strict: false });
  const search = useRouterState({
    select: (state) => state.location.search as Record<string, string>,
  });
  const paymentIdString = params.paymentid ?? search.paymentid ?? "";

  const paymentIdInt = parseInt(paymentIdString);

  if (!context) {
    throw new Error(
      "usePaymentPollingContext must be used within a PaymentPollingProvider"
    );
  }

  const { startPolling } = context;

  useEffect(() => {
    if (paymentIdInt && !isNaN(paymentIdInt)) {
      startPolling(paymentIdInt);
    }
  }, [startPolling, paymentIdInt]);

  return context;
};

type PaymentPollingProviderProps = {
  children: React.ReactNode;
};

// 5 seconds
const POLLING_INTERVAL = 5000;
// 15 minutes
const POLLING_TIMEOUT = 60 * 5 * 1000;

/**
 * After payment payment is initiated we start polling for payment status in a *provider*,
 * so that even if user navigates away from the page, the polling continues and plan is updated.
 *
 * It times out after 15 minutes. It's used as part of the checkout flow.
 */
export const PaymentPollingProvider = ({
  children,
}: PaymentPollingProviderProps) => {
  const pollingId = React.useRef(-1);
  const timeoutPollingId = React.useRef(-1);
  const [paymentId, setPaymentId] = React.useState<number | null>(null);
  const [timedOutPaymentId, setTimedOutPaymentId] = React.useState<
    number | null
  >(null);
  const { mutateAll } = useSessionMutate();
  const { isLoggedIn } = useSession();
  const isProgressiveRolloutEnabled = useIsFlagEnabled(
    "payment_polling_progressive_rollout"
  );

  const matchRoute = useMatchRoute();
  // Matched, not string-compared: a trailing slash would defeat equality but
  // never reached Next, which compared the route pattern (ANO-117).
  const isCheckoutPage =
    !!matchRoute({ to: "/subscription/customize/checkout" }) ||
    !!matchRoute({ to: "/express-checkout" });

  // This ensures that we start polling for payment status when the page is loaded,
  // allowing us to detect pending payments on refresh and similar.
  useSWR<PendingPaymentsList>(
    {
      url: "/payment/pending/",
      enabled: isLoggedIn && isProgressiveRolloutEnabled,
    },
    {
      revalidateOnFocus: false,
      onSuccess: (data) => {
        const pendingOrProcessing = data?.results?.filter(
          ({ status }) => status === "processing" || status === "pending"
        );
        if (!pendingOrProcessing?.length) return;
        const { id } = pendingOrProcessing[0];
        startPolling(id);
      },
    }
  );

  const { data: paymentData, mutate: mutatePayment } =
    useSWR<PendingPaymentType>({
      url: `/payment/pending/${paymentId}/`,
      enabled: !!paymentId,
    });

  const {
    transaction: transactionId,
    status,
    plan: planID,
  } = paymentData ?? {};

  const { data: planResponse } = useSWR<PlanType>({
    url: `/subscription/plan/${planID}/`,
    enabled: !!planID,
  });

  const { data: transactionData, mutate: mutateTransaction } =
    useSWR<TransactionType>({
      url: `/payment/transaction/${transactionId}/`,
      enabled: !!transactionId,
    });

  // When the payment has changed to successful, we update all the relevant data.
  useEffect(() => {
    if (status === "successful") {
      mutateTransaction();
      mutateAll();
      isPollingActiveRef.current = false;
      clearInterval(pollingId.current);
      clearTimeout(timeoutPollingId.current);
    }
  }, [status, mutateTransaction, mutateAll, paymentId]);

  // mutatePayment is held in a ref so the interval below always sees the
  // latest mutator without needing to re-arm the interval on every render.
  const mutatePaymentRef = React.useRef(mutatePayment);
  mutatePaymentRef.current = mutatePayment;

  // Track whether polling is "armed" — the interval reads this to decide
  // whether to fire. We use a ref instead of capturing paymentId in the
  // closure: startPolling sets the ref synchronously, so the interval it
  // schedules right after sees the active id immediately. (The previous
  // implementation captured paymentId at call time, which was always the
  // pre-setState value of null — so the interval ticked but never polled.)
  const isPollingActiveRef = React.useRef(false);

  /**
   * This function is executed when `usePaymentPollingContext` is called.
   *
   * It ensures there is 1 polling interval running at any time.
   * It also initiates a timeout to stop polling after 5 minutes.
   */
  const startPolling = (_paymentId: number) => {
    if (!_paymentId || isNaN(_paymentId)) return;
    if (status === "successful" && paymentId === _paymentId) {
      return;
    }
    setPaymentId(_paymentId);
    setTimedOutPaymentId(null);
    clearInterval(pollingId.current);
    clearTimeout(timeoutPollingId.current);

    isPollingActiveRef.current = true;

    const tick = async () => {
      if (!isPollingActiveRef.current) return;
      try {
        await mutatePaymentRef.current();
      } catch (error) {
        captureException(error);
      }
    };

    // Start polling for payment status
    pollingId.current = window.setInterval(tick, POLLING_INTERVAL);
    tick();
    // Stop polling after 5 minutes
    timeoutPollingId.current = window.setTimeout(() => {
      isPollingActiveRef.current = false;
      clearInterval(pollingId.current);
      setTimedOutPaymentId(_paymentId);
      setPaymentId(null);
    }, POLLING_TIMEOUT);
  };

  const showSnackbar =
    status &&
    status === "processing" &&
    // We don't want to show a "Processing Payment" message on checkout pages, because user might be adding a card
    !isCheckoutPage;

  return (
    <PaymentPollingContext.Provider
      value={{
        startPolling,
        transactionData,
        planResponse,
        paymentData,
        mutatePayment,
        mutateTransaction,
        paymentId,
        status,
        timedOutPaymentId,
      }}
    >
      <Snackbar open={showSnackbar}>
        <div>
          <Alert
            severity="warning"
            icon={<CircularProgress sx={{ mt: 1 }} color="warning" size={16} />}
          >
            <Typography>Please wait while we process your payment.</Typography>
          </Alert>
        </div>
      </Snackbar>
      {children}
    </PaymentPollingContext.Provider>
  );
};
